/**
 * Rent Increase Detail Page
 * Checklist view for a single rent increase (lease-lifecycle — The Rent Increase Process,
 * J-RI-SERVICE). The header names the unit, tenants and how notices are served (BR-LL-32). A DRAFT
 * can be edited (the shared RentIncreaseForm in edit mode) or activated; an ACTIVE increase shows,
 * per step, what is scheduled, what was emailed to each tenant and any bounce that reopened it
 * (BR-LL-35/36), with "Generate document", "Mark served" (records service, sends nothing) and — in
 * MANUAL mode — "Send email to tenants…" (confirms recipients first).
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Check, Download, Eye, X } from 'lucide-react';
import {
  getRentIncrease,
  getLease,
  activateRentIncrease,
  updateRentIncrease,
  cancelRentIncrease,
  generateRentIncreaseStepDocument,
  completeRentIncreaseStep,
  sendRentIncreaseStepEmail,
  previewRentIncrease,
  documentsApi,
  ApiClientError,
  type RentIncreaseResponse,
  type RentIncreaseStepResponse,
  type RentIncreasePreview,
} from '../lib/api';
import { PageHeader, PageSection, DataGrid, DataRow } from '../components/layout/index';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Modal } from '../components/Modal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import {
  RentIncreaseForm,
  RentIncreaseStatusBadge,
  formatCurrency,
  formatRentIncreaseHeading,
  getNeedsAction,
  isStepReopened,
  findPreviewStep,
  describeWarning,
  formatServiceMethods,
} from '../components/rent-increase/RentIncreaseForm';
import { formatCalendarDate, formatTimestamp, deriveTimezone } from '../utils/date';
import { todayInTimezone, asCalendarDateString, asIsoDateTimeString } from '@resida/shared';
import { useUnitTimezone } from '../hooks/useUnitTimezone';
import type { LeaseDetail } from '../types/lease';
import { readFileIntoMemory } from '@resida/shared';

const MARK_SERVED_HELPER = 'Mark served records that you served it. It does not send anything.';

type EmailOutcome = RentIncreaseStepResponse['emails'][number];

function stepStatusBadgeVariant(step: RentIncreaseStepResponse, reopened: boolean): 'secondary' | 'success' | 'danger' | 'warning' {
  if (step.status === 'COMPLETED') return 'success';
  if (step.status === 'OVERDUE') return 'danger';
  if (reopened) return 'warning';
  return 'secondary';
}

/** Names of the tenants a step's notice reached (accepted or delivered), for the post-send confirmation. */
function emailedTenantNames(step: RentIncreaseStepResponse): string[] {
  return step.emails.filter((e) => e.status === 'SENT' || e.status === 'DELIVERED').map((e) => e.tenantName);
}

/** When the first email for a step went out (ISO), or null. */
function firstEmailedAt(step: RentIncreaseStepResponse): string | null {
  return step.emails.find((e) => e.sentAt !== null)?.sentAt ?? null;
}

export function RentIncreaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [rentIncrease, setRentIncrease] = useState<RentIncreaseResponse | null>(null);
  const [unitId, setUnitId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Read-only plan preview for this increase: scheduled-email dates, who has an email, whether email service is allowed. */
  const [preview, setPreview] = useState<RentIncreasePreview | null>(null);

  const timezoneQuery = useUnitTimezone(unitId);
  const propertyTimezone = timezoneQuery.data ?? deriveTimezone('');

  const [isActivating, setIsActivating] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [generatingStepId, setGeneratingStepId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [emailingStep, setEmailingStep] = useState<RentIncreaseStepResponse | null>(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  /** Success message after a manual send, kept on the step it belongs to. */
  const [sentNotice, setSentNotice] = useState<{ stepId: string; message: string } | null>(null);

  const [servingStep, setServingStep] = useState<RentIncreaseStepResponse | null>(null);
  const [serviceDate, setServiceDate] = useState('');
  const [serviceNotes, setServiceNotes] = useState('');
  const [servicePhoto, setServicePhoto] = useState<File | null>(null);
  const [isSubmittingService, setIsSubmittingService] = useState(false);
  const [serviceError, setServiceError] = useState<string | null>(null);

  const fetchRentIncrease = useCallback(async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      setError(null);
      const result = await getRentIncrease(id);
      setRentIncrease(result);
      // getLease's declared return type (LeaseResponse, `{ lease }`) is stale for this
      // endpoint — it actually returns the flat lease object (see LeaseDetailPage's same
      // `as Promise<unknown>` / `as LeaseDetail` cast around this call).
      const lease = await (getLease('', result.leaseId) as Promise<unknown>).catch(() => null);
      if (lease) setUnitId((lease as LeaseDetail).unitId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rent increase');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRentIncrease();
  }, [fetchRentIncrease]);

  // The preview of the stored plan is advisory context (scheduled-email dates, recipients, email-service
  // permission); a failed lookup just leaves those lines out.
  const previewKey = rentIncrease
    ? `${rentIncrease.leaseId}|${rentIncrease.status}|${rentIncrease.newRentAmountCents}|${rentIncrease.effectiveDate}|${rentIncrease.serviceMode}`
    : null;
  useEffect(() => {
    if (!rentIncrease || (rentIncrease.status !== 'DRAFT' && rentIncrease.status !== 'ACTIVE')) return;
    let cancelled = false;
    previewRentIncrease(rentIncrease.leaseId, {
      newRentAmountCents: rentIncrease.newRentAmountCents,
      effectiveDate: rentIncrease.effectiveDate,
      serviceMode: rentIncrease.serviceMode,
    })
      .then((result) => {
        if (!cancelled) setPreview(result);
      })
      .catch(() => {
        if (!cancelled) setPreview(null);
      });
    return () => {
      cancelled = true;
    };
    // previewKey captures every field the request is built from.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey]);

  async function handleActivate() {
    if (!id) return;
    setIsActivating(true);
    setActionError(null);
    try {
      const result = await activateRentIncrease(id);
      setRentIncrease(result);
    } catch (err) {
      setActionError(err instanceof ApiClientError ? err.message : 'Failed to activate rent increase');
    } finally {
      setIsActivating(false);
    }
  }

  async function handleConfirmCancel() {
    if (!id) return;
    setIsCancelling(true);
    setActionError(null);
    try {
      const result = await cancelRentIncrease(id);
      setRentIncrease(result);
    } catch (err) {
      setActionError(err instanceof ApiClientError ? err.message : 'Failed to cancel rent increase');
    } finally {
      setIsCancelling(false);
      setConfirmingCancel(false);
    }
  }

  async function handleGenerateDocument(stepId: string) {
    if (!id) return;
    setGeneratingStepId(stepId);
    setActionError(null);
    try {
      await generateRentIncreaseStepDocument(id, stepId);
      await fetchRentIncrease();
    } catch (err) {
      setActionError(err instanceof ApiClientError ? err.message : 'Failed to generate document');
    } finally {
      setGeneratingStepId(null);
    }
  }

  async function handleConfirmSendEmail() {
    if (!id || !emailingStep) return;
    const stepId = emailingStep.id;
    setIsSendingEmail(true);
    setActionError(null);
    setSentNotice(null);
    try {
      const updatedStep = await sendRentIncreaseStepEmail(id, stepId);
      setRentIncrease((current) =>
        current ? { ...current, steps: current.steps.map((s) => (s.id === stepId ? updatedStep : s)) } : current
      );
      const reached = emailedTenantNames(updatedStep);
      setSentNotice({
        stepId,
        message:
          reached.length > 0
            ? `Emailed ${reached.join(', ')}. Mark the step served once you're satisfied it was served.`
            : 'The email was not delivered to anyone. See the delivery lines below.',
      });
    } catch (err) {
      setActionError(err instanceof ApiClientError ? err.message : 'Failed to send email');
    } finally {
      setIsSendingEmail(false);
      setEmailingStep(null);
    }
  }

  // Edit mode is addressable (?edit=1), so the back link, browser Back and a reload all behave.
  function enterEdit() {
    setActionError(null);
    navigate({ search: '?edit=1' }, { state: { editFromDetail: true } });
  }

  function exitEdit() {
    if ((location.state as { editFromDetail?: boolean } | null)?.editFromDetail) navigate(-1);
    else navigate({ search: '' }, { replace: true });
  }

  function openMarkServed(step: RentIncreaseStepResponse) {
    setServingStep(step);
    setServiceDate(todayInTimezone(propertyTimezone));
    setServiceNotes('');
    setServicePhoto(null);
    setServiceError(null);
  }

  function closeMarkServed() {
    if (isSubmittingService) return;
    setServingStep(null);
  }

  async function handleSubmitService(e: React.FormEvent) {
    e.preventDefault();
    if (!id || !servingStep || !rentIncrease) return;

    setIsSubmittingService(true);
    setServiceError(null);
    try {
      let servicePhotoDocumentId: string | undefined;
      if (servicePhoto) {
        const formData = new FormData();
        formData.append('scope', 'LEASE');
        formData.append('leaseId', rentIncrease.leaseId);
        formData.append('name', `Rent increase service photo — ${servingStep.label}`);
        formData.append('file', await readFileIntoMemory(servicePhoto));
        const uploadResult = await documentsApi.upload(formData);
        servicePhotoDocumentId = uploadResult.document.id;
      }

      await completeRentIncreaseStep(id, servingStep.id, {
        serviceDate: serviceDate || undefined,
        serviceNotes: serviceNotes.trim() || undefined,
        servicePhotoDocumentId,
      });
      setServingStep(null);
      await fetchRentIncrease();
    } catch (err) {
      setServiceError(err instanceof ApiClientError ? err.message : err instanceof Error ? err.message : 'Failed to mark step served');
    } finally {
      setIsSubmittingService(false);
    }
  }

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto pb-6 px-[var(--page-padding-x)] pt-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-32 w-full mt-4" />
        <Skeleton className="h-16 w-full mt-3" />
        <Skeleton className="h-16 w-full mt-3" />
      </div>
    );
  }

  if (error || !rentIncrease) {
    return (
      <div className="max-w-2xl mx-auto pb-6" data-testid="rent-increase-detail-page">
        <div className="px-[var(--page-padding-x)]">
          <PageHeader
            title="Rent Increase"
            backLink={{ label: 'Back to Rent Increases', to: '/rent-increases' }}
          />
        </div>
        <div className="mx-[var(--page-padding-x)] rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm" role="alert">
          <p className="text-destructive m-0">{error ?? 'Rent increase not found'}</p>
          <Button
            onClick={fetchRentIncrease}
            type="button"
            variant="outline"
            className="mt-2"
            data-testid="rent-increase-detail-retry-button"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const canActivate = rentIncrease.status === 'DRAFT';
  const canCancel = rentIncrease.status === 'DRAFT' || rentIncrease.status === 'ACTIVE';
  const isAutomatic = rentIncrease.serviceMode === 'AUTOMATIC_EMAIL';
  const tenantNames = rentIncrease.context.tenants.map((t) => t.name);
  const needsAction = getNeedsAction(rentIncrease);
  /** "Send email to tenants…" only makes sense in MANUAL mode where the jurisdiction permits email service. */
  const emailServiceOpen =
    rentIncrease.status === 'ACTIVE' && rentIncrease.serviceMode === 'MANUAL' && preview?.electronicServicePermitted === true;
  /**
   * BR-LL-41: where tenant consent is required, an email can only go out if the landlord attested when the plan was
   * made (the attestation freezes at activation). Without it the server would reject the send, so it is not offered.
   */
  const consentMissing = preview?.consentAttestationRequired === true && rentIncrease.emailConsentAttestedAt === null;
  const canOfferSendEmail = emailServiceOpen && !consentMissing;
  const today = todayInTimezone(propertyTimezone);
  const isEditing = searchParams.get('edit') === '1' && rentIncrease.status === 'DRAFT';

  const shortDate = (iso: string | null) =>
    iso ? formatTimestamp(asIsoDateTimeString(iso), propertyTimezone, { month: 'short', day: 'numeric' }) : null;

  if (isEditing) {
    return (
      <div className="max-w-5xl mx-auto pb-6" data-testid="rent-increase-detail-page">
        <div className="px-[var(--page-padding-x)]">
          <PageHeader
            title="Edit rent increase"
            backLink={{ label: 'Back to rent increase', to: `/rent-increases/${rentIncrease.id}` }}
          />
          <RentIncreaseForm
            mode="edit"
            leaseId={rentIncrease.leaseId}
            unitId={rentIncrease.context.unit.id}
            timezone={propertyTimezone}
            currentRentCents={rentIncrease.currentRentAmountCents}
            context={{
              propertyName: rentIncrease.context.property.name,
              unitNumber: rentIncrease.context.unit.unitNumber,
              tenantNames,
            }}
            initialValues={{
              newRentAmountCents: rentIncrease.newRentAmountCents,
              effectiveDate: rentIncrease.effectiveDate,
              autoUpdateLease: rentIncrease.autoUpdateLease,
              serviceMode: rentIncrease.serviceMode,
              emailConsentAttested: rentIncrease.emailConsentAttestedAt !== null,
            }}
            onSubmit={async (values) => {
              // The server surfaces 409 once a document exists; the form shows that message.
              const result = await updateRentIncrease(rentIncrease.id, values);
              setRentIncrease(result);
              exitEdit();
            }}
            onCancel={exitEdit}
          />
        </div>
      </div>
    );
  }

  const recipientsToEmail = preview ? preview.recipients.filter((r) => r.hasEmail).map((r) => r.name) : tenantNames;
  const recipientsWithoutEmail = preview ? preview.recipients.filter((r) => !r.hasEmail).map((r) => r.name) : [];

  return (
    <div className="max-w-2xl mx-auto pb-6" data-testid="rent-increase-detail-page">
      <div className="px-[var(--page-padding-x)] md:px-0">
        <PageHeader
          title={formatRentIncreaseHeading(rentIncrease.context.property.name, rentIncrease.context.unit.unitNumber)}
          subtitle={[
            tenantNames.join(', ') || null,
            `${formatCurrency(rentIncrease.currentRentAmountCents)} → ${formatCurrency(rentIncrease.newRentAmountCents)}`,
            `effective ${formatCalendarDate(asCalendarDateString(rentIncrease.effectiveDate))}`,
          ]
            .filter((part): part is string => part !== null)
            .join(' · ')}
          subtitleWrap
          backLink={{ label: 'Back to Rent Increases', to: '/rent-increases' }}
          action={<RentIncreaseStatusBadge status={rentIncrease.status} data-testid="rent-increase-status-badge" />}
        />

        <p className="m-0 mb-3 text-sm" data-testid="rent-increase-service-mode">
          <strong className="text-foreground">{isAutomatic ? 'Serving by automatic email.' : 'Serving manually.'}</strong>{' '}
          <span className="text-muted-foreground">
            {isAutomatic
              ? rentIncrease.status === 'DRAFT'
                ? 'The mode is fixed when you activate.'
                : 'Fixed when activated. Steps below show exactly what was sent and what is scheduled.'
              : 'You serve each notice yourself, then mark the step served.'}
          </span>
        </p>

        {rentIncrease.status === 'DRAFT' && (
          <p className="m-0 mb-3 text-[13px] text-muted-foreground">
            A draft sends nothing. You can change any of this until you activate it.
          </p>
        )}

        {(canActivate || canCancel) && (
          <div className="mb-4 flex flex-wrap gap-2">
            {canActivate && (
              <>
                <Button onClick={handleActivate} disabled={isActivating} data-testid="rent-increase-activate-button">
                  {isActivating ? 'Activating...' : 'Activate'}
                </Button>
                <Button onClick={enterEdit} variant="outline" data-testid="rent-increase-edit-button">
                  Edit
                </Button>
              </>
            )}
            {canCancel && (
              <Button
                onClick={() => {
                  setActionError(null);
                  setConfirmingCancel(true);
                }}
                disabled={isCancelling}
                variant="ghost"
                className={`text-destructive hover:text-destructive hover:bg-destructive/10 ${canActivate ? '' : '-ml-4'}`}
                data-testid="rent-increase-cancel-button"
              >
                {isCancelling ? 'Cancelling...' : 'Cancel rent increase'}
              </Button>
            )}
          </div>
        )}
      </div>

      {actionError && (
        <div className="mx-[var(--page-padding-x)] md:mx-0 mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm" role="alert" data-testid="rent-increase-action-error">
          <p className="text-destructive m-0">{actionError}</p>
        </div>
      )}

      {needsAction.map((item) => (
        <div
          key={item.stepId}
          className="mx-[var(--page-padding-x)] md:mx-0 mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-foreground"
          role="alert"
          data-testid={`rent-increase-needs-action-${item.stepId}`}
        >
          <strong className="text-destructive">Needs your action.</strong>{' '}
          {item.kind === 'BOUNCED'
            ? `The email to ${item.tenantNames.join(', ')} for step ${item.stepNumber} bounced${
                shortDate(item.bouncedAt) ? ` on ${shortDate(item.bouncedAt)}` : ''
              }.${item.reopened ? ' The step was reopened.' : ''} Serve the notice another way, then mark it served.`
            : `Step ${item.stepNumber} (${item.stepLabel}) is overdue. Serve it, then mark it served.`}
        </div>
      ))}

      <PageSection title="Summary" headingLevel={2}>
        <DataGrid columns={2}>
          <DataRow label="Current Rent" value={formatCurrency(rentIncrease.currentRentAmountCents)} />
          <DataRow label="New Rent" value={formatCurrency(rentIncrease.newRentAmountCents)} />
          <DataRow label="Effective Date" value={formatCalendarDate(asCalendarDateString(rentIncrease.effectiveDate))} />
          <DataRow label="Auto-Update Lease" value={rentIncrease.autoUpdateLease ? 'Yes' : 'No'} />
        </DataGrid>
        {rentIncrease.warnings.length > 0 && (
          <ul className="mt-3 flex flex-col gap-1.5 list-none p-0 m-0" data-testid="rent-increase-warnings">
            {rentIncrease.warnings.map((w, i) => (
              <li
                key={i}
                className="text-xs text-warning bg-warning/10 border border-warning/25 rounded-md px-2.5 py-1.5"
              >
                {describeWarning(w, preview?.steps, today, rentIncrease.effectiveDate)}
              </li>
            ))}
          </ul>
        )}
      </PageSection>

      <PageSection title="Steps" headingLevel={2} className="md:mt-4">
        <ol className="m-0 flex list-none flex-col gap-3 p-0">
          {rentIncrease.steps.map((step, index) => {
            const previewStep = preview ? findPreviewStep(preview, step) : undefined;
            const isOpen = step.status === 'PENDING' || step.status === 'OVERDUE';
            const reopened = isStepReopened(rentIncrease, step);
            const requiresExecution = previewStep?.requiresExecution === true || step.signingEnvelopeId !== null;
            const canGenerateDocument =
              rentIncrease.status === 'ACTIVE' && !step.generatedDocumentId && !step.signingEnvelopeId;
            const canMarkServed = rentIncrease.status === 'ACTIVE' && isOpen;
            // A signature-backed step cannot be served before its document exists.
            const markServedBlocked = requiresExecution && step.signingEnvelopeId === null;
            const canSendEmail = canOfferSendEmail && isOpen && !requiresExecution;
            // Emails already went out for this step (MANUAL send): the action says so rather than inviting a duplicate.
            const alreadyEmailed = emailedTenantNames(step);
            const sentAtFirst = firstEmailedAt(step);
            const consentBlocksEmail = emailServiceOpen && consentMissing && isOpen && !requiresExecution;
            const scheduledAt = previewStep?.scheduledEmailAt ?? null;
            const showSchedule = isAutomatic && step.status === 'PENDING' && step.emails.length === 0 && scheduledAt;
            const hasDocument = step.generatedDocumentId !== null;
            const hasActions = canGenerateDocument || canSendEmail || canMarkServed || hasDocument || step.signingEnvelopeId !== null;
            // The next thing the landlord can actually do carries the emphasis; a disabled button never does.
            const generateIsPrimary = canGenerateDocument && markServedBlocked;
            const sentMessage = sentNotice?.stepId === step.id ? sentNotice.message : null;

            return (
              <li
                key={step.id}
                className="flex flex-col gap-3 rounded-lg border border-border p-3"
                data-testid={`rent-increase-step-${step.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-foreground m-0">
                      {index + 1} · {step.label}
                    </h3>
                    <p className="text-[13px] text-muted-foreground mt-0.5 m-0">
                      Due {formatCalendarDate(asCalendarDateString(step.dueDate))}
                      {step.serviceMethods.length > 0 ? ` · ${formatServiceMethods(step.serviceMethods)}` : ''}
                      {isAutomatic && requiresExecution && isOpen ? ' · requires your signature, so always manual' : ''}
                    </p>
                    {step.serviceDate && (
                      <p className="text-[13px] text-muted-foreground mt-0.5 m-0">
                        Served {formatCalendarDate(asCalendarDateString(step.serviceDate))}
                        {step.servedLate ? ' (late)' : ''}
                        {step.completedBy === 'SYSTEM' ? ' · marked served by the system' : ''}
                      </p>
                    )}
                  </div>
                  <Badge variant={stepStatusBadgeVariant(step, reopened)} className="shrink-0" data-testid={`rent-increase-card-status-${step.id}`}>
                    {reopened ? 'REOPENED · PENDING' : step.status}
                  </Badge>
                </div>

                {step.emails.length > 0 && (
                  <ul className="m-0 flex list-none flex-col gap-1 p-0 text-[13px]" data-testid={`rent-increase-card-emails-${step.id}`}>
                    {step.emails.map((email) => (
                      <li key={email.tenantId} data-testid={`rent-increase-card-email-${step.id}-${email.tenantId}`}>
                        <EmailOutcomeLine email={email} shortDate={shortDate} />
                      </li>
                    ))}
                  </ul>
                )}

                {reopened && step.reopenedAt && (
                  <p className="m-0 text-[13px] text-muted-foreground" data-testid={`rent-increase-card-history-${step.id}`}>
                    History is kept: the emails above went out, the step was marked served by the system, and it was
                    reopened on {shortDate(step.reopenedAt)} because an email bounced.
                  </p>
                )}

                {sentMessage && (
                  <p
                    role="status"
                    className="m-0 rounded-lg border border-success/25 bg-success/10 px-3 py-2 text-[13px] text-success"
                    data-testid={`rent-increase-card-sent-${step.id}`}
                  >
                    {sentMessage}
                  </p>
                )}

                {showSchedule && (
                  <p className="m-0 text-[13px] text-foreground" data-testid={`rent-increase-card-scheduled-${step.id}`}>
                    Will email {recipientsToEmail.length > 0 ? recipientsToEmail.join(', ') : 'your tenants'} on{' '}
                    <strong>{formatCalendarDate(asCalendarDateString(scheduledAt))}</strong>.
                    {recipientsWithoutEmail.length > 0 &&
                      ` ${recipientsWithoutEmail.join(', ')} ${recipientsWithoutEmail.length === 1 ? 'has' : 'have'} no email address on file and will not be emailed: serve by document.`}
                  </p>
                )}

                {isAutomatic && requiresExecution && isOpen && (
                  <p className="m-0 text-[13px] text-muted-foreground">
                    Not sent automatically. Generate the document, sign it, and serve it yourself.
                  </p>
                )}

                {consentBlocksEmail && (
                  <p className="m-0 text-[13px] text-muted-foreground" data-testid={`rent-increase-card-email-consent-${step.id}`}>
                    You can&apos;t email this notice: your tenants&apos; written consent to email service wasn&apos;t
                    recorded when this increase was planned. Serve it another way, then mark it served.
                  </p>
                )}

                {step.documentKind === 'JURISDICTION' && (
                  <p className="m-0 text-xs text-muted-foreground" data-testid={`rent-increase-card-jurisdiction-${step.id}`}>
                    Document: the form this jurisdiction requires.
                  </p>
                )}

                {hasActions && (
                  <div className="flex flex-wrap gap-2">
                    {canGenerateDocument && (
                      <Button
                        onClick={() => handleGenerateDocument(step.id)}
                        disabled={generatingStepId === step.id}
                        variant={generateIsPrimary ? 'default' : 'secondary'}
                        data-testid={`rent-increase-generate-document-${step.id}`}
                      >
                        {generatingStepId === step.id ? 'Generating...' : 'Generate document'}
                      </Button>
                    )}
                    {step.generatedDocumentId && (
                      <>
                        <Button variant="outline" asChild>
                          <a
                            href={documentsApi.viewUrl(step.generatedDocumentId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`rent-increase-view-document-${step.id}`}
                          >
                            <Eye aria-hidden />
                            View notice
                            <span className="sr-only"> (opens in a new tab)</span>
                          </a>
                        </Button>
                        <Button variant="outline" asChild>
                          <a
                            href={documentsApi.downloadUrl(step.generatedDocumentId)}
                            download
                            data-testid={`rent-increase-download-document-${step.id}`}
                          >
                            <Download aria-hidden />
                            Download notice
                          </a>
                        </Button>
                      </>
                    )}
                    {step.signingEnvelopeId && (
                      <Button variant="outline" asChild>
                        <Link to={`/signing/${step.signingEnvelopeId}`} data-testid={`rent-increase-signing-link-${step.id}`}>
                          {step.status === 'COMPLETED' ? 'View signing' : 'Open for signing'}
                        </Link>
                      </Button>
                    )}
                    {canSendEmail && (
                      <Button
                        onClick={() => {
                          setActionError(null);
                          setEmailingStep(step);
                        }}
                        variant="outline"
                        data-testid={`rent-increase-send-email-${step.id}`}
                      >
                        {alreadyEmailed.length > 0 ? 'Send again…' : 'Send email to tenants…'}
                      </Button>
                    )}
                    {canMarkServed && (
                      <Button
                        onClick={() => openMarkServed(step)}
                        disabled={markServedBlocked}
                        variant={markServedBlocked ? 'outline' : 'default'}
                        aria-describedby={`rent-increase-mark-served-help-${step.id}`}
                        data-testid={`rent-increase-mark-served-${step.id}`}
                      >
                        Mark served
                      </Button>
                    )}
                  </div>
                )}
                {canMarkServed && (
                  <p id={`rent-increase-mark-served-help-${step.id}`} className="m-0 text-xs text-muted-foreground">
                    {markServedBlocked ? 'Generate and sign the document first. ' : ''}
                    {MARK_SERVED_HELPER}
                  </p>
                )}
                {canSendEmail && alreadyEmailed.length > 0 && sentAtFirst && (
                  <p className="m-0 text-xs text-muted-foreground">
                    Already emailed to {alreadyEmailed.join(', ')} on {shortDate(sentAtFirst)}.
                  </p>
                )}
              </li>
            );
          })}
        </ol>
      </PageSection>

      <ConfirmDialog
        isOpen={!!emailingStep}
        onClose={() => {
          if (!isSendingEmail) setEmailingStep(null);
        }}
        onConfirm={handleConfirmSendEmail}
        title={emailingStep && emailedTenantNames(emailingStep).length > 0 ? 'Send the email again?' : 'Send email to tenants?'}
        confirmText={emailingStep && emailedTenantNames(emailingStep).length > 0 ? 'Send again' : 'Send email'}
        isLoading={isSendingEmail}
        message={
          emailingStep && (
            <div data-testid="rent-increase-send-email-confirm">
              {emailedTenantNames(emailingStep).length > 0 && (
                <p className="mb-2 mt-0 font-medium text-warning" data-testid="rent-increase-send-email-already">
                  This notice was already emailed to {emailedTenantNames(emailingStep).join(', ')}
                  {shortDate(firstEmailedAt(emailingStep)) ? ` on ${shortDate(firstEmailedAt(emailingStep))}` : ''}
                  . Sending again emails them a second copy.
                </p>
              )}
              <p className="m-0">
                We will email the {emailingStep.label} to{' '}
                <strong data-testid="rent-increase-send-email-recipients">
                  {recipientsToEmail.length > 0 ? recipientsToEmail.join(', ') : 'your tenants'}
                </strong>
                .
              </p>
              {recipientsWithoutEmail.length > 0 && (
                <p className="mb-0 mt-2 text-warning">
                  {recipientsWithoutEmail.join(', ')} {recipientsWithoutEmail.length === 1 ? 'has' : 'have'} no email address
                  on file and will not receive it.
                </p>
              )}
              <p className="mb-0 mt-2 text-muted-foreground">
                Sending does not mark the step served. Do that separately once the notice is served.
              </p>
            </div>
          )
        }
      />

      <ConfirmDialog
        isOpen={confirmingCancel}
        onClose={() => {
          if (!isCancelling) setConfirmingCancel(false);
        }}
        onConfirm={handleConfirmCancel}
        title="Cancel this rent increase?"
        message="Cancelling skips every step that is not yet served and stops any scheduled emails. Emails already sent cannot be recalled. This cannot be undone."
        confirmText="Cancel rent increase"
        cancelText="Keep it"
        isDestructive
        isLoading={isCancelling}
      />

      <Modal
        isOpen={!!servingStep}
        onClose={closeMarkServed}
        title="Mark step served"
        data-testid="rent-increase-mark-served-modal"
      >
        {servingStep && (
          <form onSubmit={handleSubmitService} data-testid="rent-increase-mark-served-form">
            {serviceError && (
              <div className="p-3 mb-3 text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-md" role="alert">
                {serviceError}
              </div>
            )}

            <p className="mb-3 mt-0 text-sm text-muted-foreground">{MARK_SERVED_HELPER}</p>

            <div className="mb-3">
              <label htmlFor="ri-service-date" className="block mb-1 text-sm font-medium text-muted-foreground">
                Service Date
              </label>
              <input
                id="ri-service-date"
                type="date"
                value={serviceDate}
                onChange={(e) => setServiceDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-input bg-background text-foreground rounded-md box-border min-h-[44px] focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="rent-increase-service-date-input"
              />
            </div>

            <div className="mb-3">
              <label htmlFor="ri-service-notes" className="block mb-1 text-sm font-medium text-muted-foreground">
                Notes
              </label>
              <textarea
                id="ri-service-notes"
                value={serviceNotes}
                onChange={(e) => setServiceNotes(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-input bg-background text-foreground rounded-md box-border min-h-[80px] resize-y font-[inherit] focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="How was this step served? (optional)"
                data-testid="rent-increase-service-notes-input"
              />
            </div>

            <div className="mb-4">
              <label htmlFor="ri-service-photo" className="block mb-1 text-sm font-medium text-muted-foreground">
                Proof-of-Service Photo (optional)
              </label>
              <input
                id="ri-service-photo"
                type="file"
                accept="image/*"
                onChange={(e) => setServicePhoto(e.target.files?.[0] ?? null)}
                className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded-md box-border focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="rent-increase-service-photo-input"
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={closeMarkServed}
                disabled={isSubmittingService}
                data-testid="rent-increase-mark-served-cancel"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmittingService} data-testid="rent-increase-mark-served-submit">
                {isSubmittingService ? 'Saving...' : 'Mark served'}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}

/** One tenant's emailed-notice outcome: icon + word (never colour alone), tenant, timestamps (BR-LL-35). */
function EmailOutcomeLine({ email, shortDate }: { email: EmailOutcome; shortDate: (iso: string | null) => string | null }) {
  const sent = shortDate(email.sentAt);
  const bounced = shortDate(email.bouncedAt);
  switch (email.status) {
    case 'DELIVERED':
    case 'SENT':
      return (
        <>
          <span className="inline-flex items-center gap-1 text-success">
            <Check aria-hidden className="h-3.5 w-3.5" /> {email.status === 'DELIVERED' ? 'Delivered' : 'Accepted'}
          </span>{' '}
          · {email.tenantName}
          {sent ? ` · emailed ${sent}` : ''}
        </>
      );
    case 'BOUNCED':
      return (
        <>
          <span className="inline-flex items-center gap-1 text-destructive">
            <X aria-hidden className="h-3.5 w-3.5" /> Bounced
          </span>{' '}
          · {email.tenantName}
          {sent ? ` · emailed ${sent}` : ''}
          {bounced ? `, bounced ${bounced}` : ''}
        </>
      );
    case 'FAILED':
      return (
        <>
          <span className="inline-flex items-center gap-1 text-destructive">
            <X aria-hidden className="h-3.5 w-3.5" /> Failed to send
          </span>{' '}
          · {email.tenantName}
        </>
      );
    case 'SKIPPED_NO_EMAIL':
      return (
        <span className="text-muted-foreground">
          Not emailed: no email on file · {email.tenantName}
        </span>
      );
    case 'SCHEDULED':
      return (
        <span className="text-muted-foreground">
          Scheduled · {email.tenantName}
        </span>
      );
  }
}

export default RentIncreaseDetailPage;
